/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { injectIntl } from 'react-intl'
import { forEach } from 'lodash'

import { EuiPanel, EuiSuperSelect, EuiText, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { ElasticsearchClusterTrustSettings } from '@modules/cloud-api/v1/types'
import type { AnyTrustRelationship } from '@modules/ui-types'

import { envTypes, flattenTrustedEnvs, getTargetEnvironmentType } from '../../helpers'

import type { WrappedComponentProps } from 'react-intl'

export type Props = {
  onSelected: (value) => void
  trustedEnvs: ElasticsearchClusterTrustSettings
}

type State = {
  selected: any | null
}

class ReusableTrustRelationships extends React.Component<Props & WrappedComponentProps, State> {
  state = {
    selected: ``,
  }

  render() {
    return (
      <EuiPanel color='plain' hasBorder={true} data-test-id='reusable-trust-relationships-wrapper'>
        <EuiText size='s'>
          <FormattedMessage
            id='reusable-trust-relationships.description'
            defaultMessage='Select an environment already trusted by other deployments in this organization.'
          />
          <EuiSpacer size='s' />
        </EuiText>
        {this.renderContent()}
      </EuiPanel>
    )
  }

  renderContent() {
    const selectOptions = this.buildSelectOptions()

    return (
      <EuiSuperSelect
        options={selectOptions}
        hasDividers={true}
        itemLayoutAlign='top'
        valueOfSelected={this.state.selected}
        onChange={(value) => this.onChange(value)}
      />
    )
  }

  onChange(value) {
    const { onSelected } = this.props
    this.setState({ selected: value })

    const flatTrustedEnvs = flattenTrustedEnvs(this.props.trustedEnvs)

    const fullTrustedEnvObj: AnyTrustRelationship | undefined = flatTrustedEnvs.find(
      (env) => env.name === value,
    )
    onSelected(fullTrustedEnvObj)
  }

  buildSelectOptions() {
    let selectOptions = []
    const { trustedEnvs } = this.props

    forEach(envTypes, (type) => {
      selectOptions = selectOptions.concat(this.buildSelectOptionsPerType(type, trustedEnvs))
    })

    return selectOptions
  }

  buildSelectOptionsPerType(type, trustedEnvs) {
    if (!trustedEnvs[type]) {
      return []
    }

    return trustedEnvs[type].map((env) => {
      if (!env.name) {
        // this shouldn't happen as all "nameless" items should be given a default name by the API, but still a good sanity
        env.name = `Untitled`
      }

      return {
        value: env.name,
        inputDisplay: env.name,
        dropdownDisplay: (
          <Fragment>
            <strong>{env.name}</strong>
            <EuiText size='s' color='subdued'>
              <p>{this.getEnvType(env)}</p>
            </EuiText>
          </Fragment>
        ),
      }
    })
  }

  getEnvType(env) {
    const type = getTargetEnvironmentType(env)

    switch (type) {
      case 'ece':
        return `Elastic Cloud Enterprise`
      case 'ess':
        return `Elastic Cloud`
      case `self-managed`:
        return `Self-managed`
      default:
        return `Elastic Cloud`
    }
  }
}

export default injectIntl(ReusableTrustRelationships)
